import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ASPECT_RATIOS,
  findClosestAspectRatio,
  type AspectRatioKey,
} from '@/shared/constants/aspect-ratios';
import type {
  CropArea,
  ResizeHandle,
  ImageNaturalSize,
  DragState,
} from './types';

interface UseCropInteractionProps {
  imageRef: React.RefObject<HTMLImageElement | null>;
  imageNaturalSize: ImageNaturalSize;
  aspectRatio?: number;
  minWidth: number;
  minHeight: number;
  onCropAreaChange: (area: CropArea) => void;
  cropArea: CropArea;
  /**
   * When true, resizing is free-form during the drag and snaps to the nearest
   * supported ratio on release (selector mode). When false, the frame stays
   * locked to `aspectRatio` throughout the drag (fixed-ratio mode).
   */
  snapToNearestRatio?: boolean;
  /** Called on release with the ratio the freed frame snapped to. */
  onRatioSnap?: (key: AspectRatioKey) => void;
}

interface UseCropInteractionReturn {
  isDragging: boolean;
  isResizing: ResizeHandle | null;
  handleMouseDown: (e: React.MouseEvent, handle?: ResizeHandle) => void;
  handleTouchStart: (e: React.TouchEvent, handle?: ResizeHandle) => void;
}

// Calculate new crop area based on resize handle and delta
function calculateResizeArea(
  handle: ResizeHandle,
  prev: CropArea,
  deltaX: number,
  deltaY: number,
  minWidth: number,
  minHeight: number
): CropArea {
  const newArea = { ...prev };

  switch (handle) {
    case 'nw': {
      const maxDeltaX = prev.width - minWidth;
      const maxDeltaY = prev.height - minHeight;
      const actualDeltaX = Math.min(deltaX, maxDeltaX);
      const actualDeltaY = Math.min(deltaY, maxDeltaY);
      newArea.x += actualDeltaX;
      newArea.y += actualDeltaY;
      newArea.width -= actualDeltaX;
      newArea.height -= actualDeltaY;
      break;
    }
    case 'ne': {
      const maxDeltaY = prev.height - minHeight;
      const actualDeltaY = Math.min(deltaY, maxDeltaY);
      newArea.y += actualDeltaY;
      newArea.width += deltaX;
      newArea.height -= actualDeltaY;
      break;
    }
    case 'sw': {
      const maxDeltaX = prev.width - minWidth;
      const actualDeltaX = Math.min(deltaX, maxDeltaX);
      newArea.x += actualDeltaX;
      newArea.width -= actualDeltaX;
      newArea.height += deltaY;
      break;
    }
    case 'se':
      newArea.width += deltaX;
      newArea.height += deltaY;
      break;
    case 'n': {
      const maxDeltaY = prev.height - minHeight;
      const actualDeltaY = Math.min(deltaY, maxDeltaY);
      newArea.y += actualDeltaY;
      newArea.height -= actualDeltaY;
      break;
    }
    case 's':
      newArea.height += deltaY;
      break;
    case 'w': {
      const maxDeltaX = prev.width - minWidth;
      const actualDeltaX = Math.min(deltaX, maxDeltaX);
      newArea.x += actualDeltaX;
      newArea.width -= actualDeltaX;
      break;
    }
    case 'e':
      newArea.width += deltaX;
      break;
  }

  return newArea;
}

// Coerce a free-resized area to an exact ratio, keeping the dragged handle's
// anchor (the opposite edge/corner) fixed so the frame snaps in place rather
// than jumping back to a centered crop. Bounds are enforced separately by
// constrainCropArea.
function snapAreaToRatio(
  handle: ResizeHandle,
  area: CropArea,
  ratio: number
): CropArea {
  // Top/bottom edges drive height; everything else drives width.
  const horizontal = handle === 'n' || handle === 's';
  const width = horizontal ? area.height * ratio : area.width;
  const height = horizontal ? area.height : area.width / ratio;

  let x = area.x;
  if (handle === 'nw' || handle === 'sw' || handle === 'w') {
    x = area.x + area.width - width; // right edge fixed
  } else if (horizontal) {
    x = area.x + (area.width - width) / 2; // center horizontally
  } // ne/se/e: left edge fixed → x unchanged

  let y = area.y;
  if (handle === 'nw' || handle === 'ne' || handle === 'n') {
    y = area.y + area.height - height; // bottom edge fixed
  } else if (handle === 'e' || handle === 'w') {
    y = area.y + (area.height - height) / 2; // center vertically
  } // sw/se/s: top edge fixed → y unchanged

  return { x, y, width, height };
}

export function useCropInteraction({
  imageRef,
  imageNaturalSize,
  aspectRatio,
  minWidth,
  minHeight,
  onCropAreaChange,
  cropArea,
  snapToNearestRatio = false,
  onRatioSnap,
}: UseCropInteractionProps): UseCropInteractionReturn {
  // In snap mode the frame is unlocked during the drag (ratio enforced only on
  // release); otherwise it stays pinned to `aspectRatio` the whole time.
  const lockRatio = snapToNearestRatio ? undefined : aspectRatio;
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<ResizeHandle | null>(null);
  const dragStartRef = useRef<DragState>({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<{ deltaX: number; deltaY: number } | null>(
    null
  );
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef<ResizeHandle | null>(null);

  const constrainCropArea = useCallback(
    (area: CropArea, ratio?: number): CropArea => {
      const constrained = { ...area };

      // First, clamp position to ensure we start within bounds
      constrained.x = Math.max(
        0,
        Math.min(constrained.x, imageNaturalSize.width)
      );
      constrained.y = Math.max(
        0,
        Math.min(constrained.y, imageNaturalSize.height)
      );

      // Ensure minimum size
      constrained.width = Math.max(constrained.width, minWidth);
      constrained.height = Math.max(constrained.height, minHeight);

      // Maintain aspect ratio if specified
      if (ratio) {
        const widthChanged = Math.abs(area.width - constrained.width) > 0.1;
        const heightChanged = Math.abs(area.height - constrained.height) > 0.1;

        if (widthChanged && !heightChanged) {
          constrained.height = constrained.width / ratio;
        } else if (heightChanged && !widthChanged) {
          constrained.width = constrained.height * ratio;
        } else {
          constrained.height = constrained.width / ratio;
        }
      }

      // Ensure crop area stays within image bounds
      if (constrained.x + constrained.width > imageNaturalSize.width) {
        constrained.width = imageNaturalSize.width - constrained.x;
        if (ratio) {
          constrained.height = constrained.width / ratio;
        }
      }
      if (constrained.y + constrained.height > imageNaturalSize.height) {
        constrained.height = imageNaturalSize.height - constrained.y;
        if (ratio) {
          constrained.width = constrained.height * ratio;
        }
      }

      // Final bounds check
      constrained.x = Math.max(
        0,
        Math.min(constrained.x, imageNaturalSize.width - constrained.width)
      );
      constrained.y = Math.max(
        0,
        Math.min(constrained.y, imageNaturalSize.height - constrained.height)
      );
      constrained.width = Math.min(
        constrained.width,
        imageNaturalSize.width - constrained.x
      );
      constrained.height = Math.min(
        constrained.height,
        imageNaturalSize.height - constrained.y
      );

      return constrained;
    },
    [minWidth, minHeight, imageNaturalSize]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, handle?: ResizeHandle) => {
      e.preventDefault();
      e.stopPropagation();

      if (handle) {
        setIsResizing(handle);
        isResizingRef.current = handle;
      } else {
        setIsDragging(true);
        isDraggingRef.current = true;
      }

      dragStartRef.current = { x: e.clientX, y: e.clientY };
    },
    []
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent, handle?: ResizeHandle) => {
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      if (!touch) return;

      if (handle) {
        setIsResizing(handle);
        isResizingRef.current = handle;
      } else {
        setIsDragging(true);
        isDraggingRef.current = true;
      }

      dragStartRef.current = { x: touch.clientX, y: touch.clientY };
    },
    []
  );

  const processUpdate = useCallback(
    (clientX: number, clientY: number) => {
      const imageRect = imageRef.current?.getBoundingClientRect();
      if (!imageRect || !imageNaturalSize.width || !imageNaturalSize.height)
        return;

      const actualScaleX = imageRect.width / imageNaturalSize.width;
      const actualScaleY = imageRect.height / imageNaturalSize.height;

      const deltaX = (clientX - dragStartRef.current.x) / actualScaleX;
      const deltaY = (clientY - dragStartRef.current.y) / actualScaleY;

      pendingUpdateRef.current = { deltaX, deltaY };

      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = null;

          const pending = pendingUpdateRef.current;
          if (!pending) return;

          if (isDraggingRef.current) {
            onCropAreaChange(
              constrainCropArea(
                {
                  ...cropArea,
                  x: cropArea.x + pending.deltaX,
                  y: cropArea.y + pending.deltaY,
                },
                lockRatio
              )
            );
          } else if (isResizingRef.current) {
            const newArea = calculateResizeArea(
              isResizingRef.current,
              cropArea,
              pending.deltaX,
              pending.deltaY,
              minWidth,
              minHeight
            );
            onCropAreaChange(constrainCropArea(newArea, lockRatio));
          }

          pendingUpdateRef.current = null;
        });
      }

      dragStartRef.current = { x: clientX, y: clientY };
    },
    [
      constrainCropArea,
      cropArea,
      imageRef,
      imageNaturalSize,
      lockRatio,
      minHeight,
      minWidth,
      onCropAreaChange,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current && !isResizingRef.current) return;
      processUpdate(e.clientX, e.clientY);
    },
    [processUpdate]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDraggingRef.current && !isResizingRef.current) return;

      try {
        e.preventDefault();
      } catch {
        // Ignore preventDefault errors in passive listeners
      }

      const touch = e.touches[0];
      if (!touch) return;

      processUpdate(touch.clientX, touch.clientY);
    },
    [processUpdate]
  );

  const handleEnd = useCallback(() => {
    const finishedHandle = isResizingRef.current;
    setIsDragging(false);
    setIsResizing(null);
    isDraggingRef.current = false;
    isResizingRef.current = null;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    pendingUpdateRef.current = null;

    // Snap a finished free resize to the nearest supported ratio, anchored at
    // the dragged handle. Moves (no handle) keep their ratio untouched.
    if (
      snapToNearestRatio &&
      finishedHandle &&
      onRatioSnap &&
      cropArea.width > 0 &&
      cropArea.height > 0
    ) {
      const nearestKey = findClosestAspectRatio(
        cropArea.width,
        cropArea.height
      );
      const targetRatio = ASPECT_RATIOS[nearestKey].ratio;
      onCropAreaChange(
        constrainCropArea(
          snapAreaToRatio(finishedHandle, cropArea, targetRatio),
          targetRatio
        )
      );
      onRatioSnap(nearestKey);
    }
  }, [
    snapToNearestRatio,
    onRatioSnap,
    cropArea,
    constrainCropArea,
    onCropAreaChange,
  ]);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleEnd);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        pendingUpdateRef.current = null;
      };
    }
    return undefined;
  }, [isDragging, isResizing, handleMouseMove, handleTouchMove, handleEnd]);

  return {
    isDragging,
    isResizing,
    handleMouseDown,
    handleTouchStart,
  };
}
